import { Link, useNavigate } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast, } from "../../components/CustomComponents/toast/toast";
import swal from "sweetalert";
import "../../components/CustomComponents/footer/footer.css";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import "./../order/ManageOrderFilters.css";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import Pagination from "../../components/CustomComponents/Pagination/pagination";

const CustomerReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const user = useSelector((state) => state.auth.user);
  const { profileData } = useSelector((state) => state.auth);
  const userIdentity = user?.userData?.userId ?? user?.userData?.email ?? CompID;
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;

  const [search, setSearch] = useState("");
  const pageKey = "customerReport";
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "20", label: "20" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Debounce timer ref for search
  const searchTimeoutRef = useRef(null);
  
  // Additional dropdown states
  const [locations, setLocations] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);

  const defaultVisibleColumns = {
    totalOrders: true,
    totalSpent: true,
    averageOrderSpent: true,
    loyaltyPoints: false,
    lastOrderDate: true,
    currentBalance: true,
    refundAmount: true,
    tipAmount: true,
    serviceCharges: true,
    extraCharges: true,
    discountAmount: true,
    // Hidden columns (can be shown via column selection)
    email: false,
    mobile: false,
    phone: true,
    address: false,
    city: false,
    country: false,
    postCode: false,
    lastOrderType: false,
    totalEarnedLoyaltyPoints: false,
    totalSpentLoyaltyPoints: false,
    deposit: false,
    maxCredit: false,
    referenceID: false,
    vatNumber: false,
    businessName: false,
    dateOfBirth: false,
    gender: false,
    title: false,
  };

  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Cust_Guid", label: "GUID" };
  
  // Default filter: "All Locations" so reports show all branches unless user selects one
  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultStaff = { value: "all", label: "All Staff" };
  const defaultDevice = { value: "all", label: "All Devices" };
  
  // Default to "This Week" if no existing filter
  const defaultDateRange = [
    moment().startOf('week').toDate(),
    moment().endOf('week').toDate(),
  ];
  
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  
  // Get filter values from Redux
  const location = preferences.location || defaultLocation;
  const staff = preferences.staff || defaultStaff;
  const device = preferences.device || defaultDevice;
  // Coerce cleared/null dates back to default "This Week"
  const dateRange = (() => {
    const stored = preferences.dateRange;
    if (!Array.isArray(stored)) return defaultDateRange;
    const startOk = stored[0] != null && moment(stored[0]).isValid();
    const endOk = stored[1] != null && moment(stored[1]).isValid();
    if (!startOk && !endOk) return defaultDateRange;
    return [
      startOk ? stored[0] : defaultDateRange[0],
      endOk ? stored[1] : defaultDateRange[1],
    ];
  })();
  
  // Get raw dates from Redux
  const [rawStartDate, rawEndDate] = dateRange;
  
  const dispatch = useDispatch();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);
  const navigate = useNavigate();

  const appSettings = useSelector((state) => state.appSettings);
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();
  
  // Get dynamic currency
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  
  // Currency symbols mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  
  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const sortByOptions = [
    { value: "Cust_Guid", label: "GUID" },
    { value: "Cust_Fname", label: "Name" },
    { value: "Cust_TotalOrderCount", label: "Total Orders" },
    { value: "Cust_TotalOrderSpent", label: "Total Spent" },
    { value: "Cust_AverageOrderSpent", label: "Average Order Spent" },
    { value: "Cust_LoyaltyPoints", label: "Loyalty Points" },
    { value: "Cust_RefundAmount", label: "Refund Amount" },
    { value: "Cust_TipAmount", label: "Tip Amount" },
    { value: "Cust_ServiceCharges", label: "Service Charges" },
    { value: "Cust_ExtraCharges", label: "Extra Charges" },
    { value: "Cust_DiscountAmount", label: "Discount Amount" },
  ];

  // Filter options: "All Locations" first, then parent + branches
  const allLocationsOption = { value: "all", label: "All Locations" };
  const locationOptions = locations.length > 0
    ? [allLocationsOption, ...locations]
    : isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)
      ? [allLocationsOption, { value: parentIdForLocation, label: companyName }]
      : [allLocationsOption];

  const staffOptions = staffData.length > 0 ? staffData : [
    { value: "all", label: "All Staff" }
  ];

  const deviceOptions = devices.length > 0 ? devices : [
    { value: "all", label: "All Devices" }
  ];

  // Handle filter changes with Redux
  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
    setPageNumber(1);
  };

  // Reset filters with Redux
  const resetFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    setSearch("");
  };

  // Clear all filters with automatic filtering
  const clearAllFilters = () => {
    resetFilters();
    setPageNumber(1);
  };

  // Handle search with automatic filtering
  const handleSearch = () => {
    setPageNumber(1);
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    fetchCustomers();
  };

  // Handle search input change with proper debouncing
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPageNumber(1);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      fetchCustomers();
    }, 500);
  };

  const handleSelectItem = (customerId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (customers[i]?.cust_Guid) {
            newSelected.add(customers[i].cust_Guid);
          }
        }
      } else {
        if (newSelected.has(customerId)) {
          newSelected.delete(customerId);
        } else {
          newSelected.add(customerId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(customers.filter(item => item != null && item.cust_Guid != null).map((item) => item.cust_Guid));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customers selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected customers?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((customerId) =>
          axiosInstance.delete(APIs.DELETE_CUSTOMER_BY_GUID, {
            params: {
              CompID,
              Cust_Guid: customerId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchCustomers();
      showSuccessToast(`${selectedItems.size} customers deleted successfully.`);
      setLoading(false);
    } catch (err) {

      showErrorToast("Failed to delete selected customers.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customers selected for editing.");
      return;
    }

    const selectedCustomerIds = Array.from(selectedItems);
    navigate(`/customer/bulkEdit`, { state: { selectedCustomerIds } });
  };

  // Fetch customers from API
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const toDate = (v) => {
        if (v == null) return null;
        const d = typeof v === 'string' ? new Date(v) : v;
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
      };
      let startDate = toDate(rawStartDate);
      let endDate = toDate(rawEndDate);

      // Single date: use that day for both. Date range: use start and end.
      if (startDate && !endDate) {
        endDate = moment(startDate).endOf('day').toDate();
        startDate = moment(startDate).startOf('day').toDate();
      } else if (endDate && !startDate) {
        startDate = moment(endDate).startOf('day').toDate();
        endDate = moment(endDate).endOf('day').toDate();
      } else if (startDate && endDate) {
        startDate = moment(startDate).startOf('day').toDate();
        endDate = moment(endDate).endOf('day').toDate();
      }

      const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
      const formattedEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

      // Location filter: only for parent; branch users see their own report only
      let locationFilterValue = null;
      if (isMain && location.value !== 'all' && location.value != null && location.value !== undefined) {
        const locValue = typeof location.value === 'number' ? location.value : parseInt(location.value, 10);
        if (!isNaN(locValue) && locValue > 0) {
          locationFilterValue = locValue;
        }
      }

      const params = {
        CompID,
        SearchTerm: search?.trim() || null,
        SortBy: sortBy.value,
        SortOrder: sortOrder.value,
        PageNumber: pageNumber,
        PageSize: pageSize.value,
        StartDate: formattedStartDate,
        EndDate: formattedEndDate,
        LocationFilter: locationFilterValue,
        StaffFilter: staff.value !== 'all' ? staff.value : null,
        DeviceFilter: device.value !== 'all' ? device.value : null,
      };

      const response = await axiosInstance.get(APIs.GET_CUSTOMER_REPORT, { params });

      // Handle different response structures
      let customersData = null;
      if (response.data && response.data.data) {
        customersData = response.data.data;
      } else if (Array.isArray(response.data)) {
        customersData = response.data;
      } else {
        customersData = [];
      }
      
      const totalCount = response.data?.totalRecords || response.data?.TotalRecords || 0;
      const totalPages = response.data?.totalPages || response.data?.TotalPages || 0;

      if (Array.isArray(customersData)) {
        // Filter out any null or undefined values and ensure cust_Guid exists
        const validCustomers = customersData
          .filter(customer => customer != null && customer.cust_Guid != null && customer.cust_Guid !== undefined)
          .map(customer => ({
            ...customer,
            cust_Guid: customer.cust_Guid || customer.Cust_Guid || customer.custGuid,
            cust_Fname: customer.cust_Fname || customer.Cust_Fname || customer.custFname || '',
            cust_Lname: customer.cust_Lname || customer.Cust_Lname || customer.custLname || '',
          }));
        setCustomers(validCustomers);
      } else {
        setCustomers([]);
      }
      setTotalRecords(totalCount);
      setTotalPages(totalPages);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching customers:', error);
      showErrorToast(error.response?.data?.message || error.message || "Failed to fetch customers.");
      setCustomers([]);
      setTotalRecords(0);
      setTotalPages(0);
      setLoading(false);
    }
  }, [CompID, isMain, search, sortBy, sortOrder, pageNumber, pageSize, rawStartDate, rawEndDate, location, staff, device]);

  // Fetch locations (company branches) – only for parent company
  // All Locations = parent + all branches; company name = parent only; each branch = that branch only
  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";

      const base = [{ value: parentId, label: compName }];

      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          if (pidNum === parentId && idNum !== parentId) return true;
          return false;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);

      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showErrorToast(error.response?.data?.message || "Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  // Fetch staff (excluding riders)
  const fetchStaff = useCallback(async () => {
    try {
      setLoadingStaff(true);
      
      // Fetch staff list
      const staffResponse = await axiosInstance.get(APIs.GET_ALL_STAFF, {
        params: {
          CompID
        }
      });
      const staffData = staffResponse.data.data || staffResponse.data || [];

      // Fetch roles to identify riders
      let riderRoleIds = new Set();
      try {
        const rolesResponse = await axiosInstance.get(APIs.GET_ROLES, {
          params: {
            CompID
          }
        });
        const rolesData = rolesResponse.data.data || rolesResponse.data || [];
        // Find all role IDs with "Rider" title (case-insensitive)
        rolesData.forEach(role => {
          if (role.r_Title && role.r_Title.toLowerCase() === 'rider') {
            riderRoleIds.add(role.comRolePermission_ID);
          }
        });
      } catch (roleError) {
        console.error('Error fetching roles:', roleError);
        // Continue without role filtering if roles can't be fetched
      }

      // Filter out staff with rider role
      const filteredStaff = staffData.filter(staffMember => {
        // Exclude if staff has a rider role
        const roleId = staffMember.comRolePermission_ID || staffMember.ComRolePermission_ID || staffMember.userRoles?.comRolePermission_ID;
        return !riderRoleIds.has(roleId);
      });

      // Transform staff to dropdown format
      const staffOptions = [
        { value: "all", label: "All Staff" },
        ...filteredStaff.map(staffMember => ({
          value: staffMember.userName,
          label: `${staffMember.firstName || staffMember.FirstName || ''} ${staffMember.lastName || staffMember.LastName || ''}`.trim()  
        }))
      ];

      setStaffData(staffOptions);
    } catch (error) {
      console.error('Error fetching staff:', error);
      showErrorToast(error.response?.data?.message || "Error loading staff");
    } finally {
      setLoadingStaff(false);
    }
  }, [CompID]);

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    try {
      setLoadingDevices(true);
      const response = await axiosInstance.get(APIs.GET_ALL_DEVICES, {
        params: {
          CompID
        }
      });
      const deviceData = response.data.data || response.data || [];

      // Transform devices to dropdown format
      const deviceOptions = [
        { value: "all", label: "All Devices" },
        ...deviceData.map(device => ({
          value: device.deviceID,
          label: device.deviceName
        }))
      ];

      setDevices(deviceOptions);
    } catch (error) {
      console.error('Error fetching devices:', error);
      showErrorToast(error.response?.data?.message || "Error loading devices");
    } finally {
      setLoadingDevices(false);
    }
  }, [CompID]);

  // Handle date range changes – single date or date range; clear restores default week
  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, null];
    const valid = (d) => d && moment(d).isValid();

    // Picker clear → restore default "This Week" (same as Clear filters)
    if (!valid(newStartDate) && !valid(newEndDate)) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
      setPageNumber(1);
      return;
    }

    if ((newStartDate != null || newEndDate != null) && !valid(newStartDate) && !valid(newEndDate)) {
      showErrorToast("Invalid date selected");
      return;
    }

    let effectiveStartDate = null;
    let effectiveEndDate = null;

    if (valid(newStartDate) && !valid(newEndDate)) {
      effectiveStartDate = moment(newStartDate).startOf('day').toDate();
      effectiveEndDate = moment(newStartDate).endOf('day').toDate();
    } else if (!valid(newStartDate) && valid(newEndDate)) {
      effectiveStartDate = moment(newEndDate).startOf('day').toDate();
      effectiveEndDate = moment(newEndDate).endOf('day').toDate();
    } else if (valid(newStartDate) && valid(newEndDate)) {
      effectiveStartDate = moment(newStartDate).startOf('day').toDate();
      effectiveEndDate = moment(newEndDate).endOf('day').toDate();
      if (effectiveStartDate > effectiveEndDate) {
        [effectiveStartDate, effectiveEndDate] = [effectiveEndDate, effectiveStartDate];
      }
    }

    dispatch(
      setFilterField({
        pageKey,
        field: "dateRange",
        value: [effectiveStartDate, effectiveEndDate],
      })
    );
    setPageNumber(1);
  };

  // Track if component has mounted and if initial preferences are loaded
  const [isInitialized, setIsInitialized] = useState(false);
  const isFirstRender = useRef(true);
  const hasInitialFetch = useRef(false);
  const preferencesInitialized = useRef(false);

  // Initialize preferences - only once on mount
  useEffect(() => {
    if (preferencesInitialized.current) return;
    
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    
    // Initialize filter fields
    if (!preferences.location) {
      dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    }
    if (!preferences.staff) {
      dispatch(setFilterField({ pageKey, field: "staff", value: defaultStaff }));
    }
    if (!preferences.device) {
      dispatch(setFilterField({ pageKey, field: "device", value: defaultDevice }));
    }
    if (!preferences.dateRange) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    }
    
    // Mark as initialized after a short delay to allow dispatches to complete
    const timer = setTimeout(() => {
      preferencesInitialized.current = true;
    }, 50);
    
    return () => clearTimeout(timer);
  }, [dispatch, pageKey]);

  // Load initial data only once after preferences are initialized
  useEffect(() => {
    // Skip if already initialized or already fetched
    if (isInitialized || hasInitialFetch.current) return;
    
    // Check if all required preferences are initialized
    if (preferences.visibleColumns && preferences.sortOrder && preferences.sortBy && 
        preferences.location && preferences.staff && preferences.device && preferences.dateRange) {
      setIsInitialized(true);
      hasInitialFetch.current = true;
      // Small delay to ensure all dispatches are processed
      const timer = setTimeout(() => {
        fetchCustomers();
      }, 150);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences.visibleColumns, preferences.sortOrder, preferences.sortBy, 
      preferences.location, preferences.staff, preferences.device, preferences.dateRange]);

  // Watch for filter changes and apply filters (but not on initial mount)
  useEffect(() => {
    // Skip if not initialized or if this is the first render
    if (!isInitialized || isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Skip if initial fetch hasn't happened yet
    if (!hasInitialFetch.current) {
      return;
    }
    
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce filter changes
    const timeoutId = setTimeout(() => {
      fetchCustomers();
    }, 300);
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, staff, device, rawStartDate, rawEndDate, isInitialized]);

  // Fetch dropdown data on mount; locations only for parent company
  useEffect(() => {
    if (isMain) fetchLocations();
    fetchStaff();
    fetchDevices();
  }, [isMain, fetchLocations, fetchStaff, fetchDevices]);

  // When user/account changes (login, logout+login as different account), reset location and refetch so data loads without refresh
  useEffect(() => {
    if (!CompID) return;
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    if (isMain) fetchLocations();
  }, [userIdentity, CompID, isMain, dispatch, pageKey]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Track pagination effect first run
  const paginationFirstRun = useRef(true);
  
  // Watch for pagination changes (but not on initial fetch)
  useEffect(() => {
    // Skip if not initialized or initial fetch hasn't happened
    if (!isInitialized || !hasInitialFetch.current) {
      return;
    }
    
    // Skip the very first run of this effect (initial mount)
    if (paginationFirstRun.current) {
      paginationFirstRun.current = false;
      return;
    }
    
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, sortBy, sortOrder, isInitialized]);


  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active"> Customer Report </li>
          </ol>
          <h1 className="page-header mb-0"> Customer Report</h1>
        </div>

        <div className="ms-auto">
          <Link to="/customer/AddCustomer">
            <button
              className="btn px-4"
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add Customer
            </button>
          </Link>
        </div>
      </div>
      {/* <div className="mb-3 d-sm-flex fw-bold">
        <div className="mt-sm-0 mt-2">
          <a href="#/" className="text-dark text-decoration-none">
            <i className="fa fa-download fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            Export
          </a>
        </div>
        <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2">
          <a href="#/" className="text-dark text-decoration-none">
            <i className="fa fa-upload fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            Import
          </a>
        </div>
        <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2 dropdown-toggle">
          <a
            href="#/"
            data-bs-toggle="dropdown"
            className="text-dark text-decoration-none"
          >
            <i className="fa fa-cog fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            More Actions
          </a>
          <div className="dropdown-menu">
            <a className="dropdown-item" href="#/">
              Action
            </a>
            <a className="dropdown-item" href="#/">
              Another action
            </a>
            <a className="dropdown-item" href="#/">
              Something else here
            </a>
            <div role="separator" className="dropdown-divider"></div>
            <a className="dropdown-item" href="#/">
              Separated link
            </a>
          </div>
        </div>
      </div> */}
      {/* Filter Bar - Top Section */}
      <div 
        className="card mb-4"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: '12px',
          border: 'none',
          boxShadow: `0 4px 20px ${themeColor}40`
        }}
      >
        <div className="card-body p-3">

          {/* Date Range and Search Section */}
          <div className="row g-3 align-items-center mb-3">
            <div className="col-xl-2 col-lg-3 col-md-6">
              <div className="filter-control">
                <div
                  style={{
                    position: 'relative',
                    zIndex: 9999,
                    width: '100%'
                  }}
                >
                  <DateRangePicker
                    startDate={rawStartDate ? (typeof rawStartDate === 'string' ? new Date(rawStartDate) : rawStartDate) : null}
                    endDate={rawEndDate ? (typeof rawEndDate === 'string' ? new Date(rawEndDate) : rawEndDate) : null}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                    className="filter-dropdown"
                    allowSingleDate={true}
                    allowDateRange={true}
                    popperProps={{
                      strategy: 'fixed',
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'viewport'
                          }
                        }
                      ]
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col-xl-8 col-lg-6 col-md-6">
              <div className="search-input filter-control">
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by name, email, phone..."
                  value={search}
                  onChange={handleSearchChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: '#333'
                  }}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-3 col-md-12">
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="clear-btn"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  <i className="fa fa-times me-1"></i>
                  Filter
                </button>

                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className="row g-3 align-items-center">
            {isMain && locationOptions.length > 1 && (
              <div className="col-xl-2 col-lg-2 col-md-6">
                <div className="filter-control">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange('location', option)}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="Location"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll={false}
                    menuShouldScrollIntoView={false}
                    closeMenuOnScroll={false}
                  />
                </div>
              </div>
            )}
            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={staff}
                  onChange={(option) => handleFilterChange('staff', option)}
                  options={staffOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingStaff}
                  placeholder="Staff"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={device}
                  onChange={(option) => handleFilterChange('device', option)}
                  options={deviceOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  isLoading={loadingDevices}
                  placeholder="Device"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="filter-control">
                <Select
                  value={sortBy}
                  onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                  options={sortByOptions}
                  styles={dropdownStyles}
                  placeholder="Sort By"
                  className="filter-dropdown"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="filter-control">
                <SortOrderDropDownSelection
                  defaultValue={sortOrder}
                  onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">

              {/* Selection Info and Actions (Shown only when customers are selected) */}
              {selectedItems.size > 0 && (
                <div className="mb-3">
                  <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} customer{selectedItems.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleBulkEdit}
                        title="Edit Selected Customers"
                      >
                        <i className="fa fa-edit me-1"></i>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Customers"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div
                className="table-responsive mt-3"
                style={{
                  minHeight: "200px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            customers.length > 0 &&
                            selectedItems.size === customers.length
                          }
                        />
                      </th>
                      <th className="pt-2 pb-2">Customer Name</th>
                      {visibleColumns.totalOrders && (
                        <th className="pt-2 pb-2 text-center">Total Orders</th>
                      )}
                      {visibleColumns.totalSpent && (
                        <th className="pt-2 pb-2 text-center">Total Spent</th>
                      )}
                      {visibleColumns.averageOrderSpent && (
                        <th className="pt-2 pb-2 text-center">Avg Order Spent</th>
                      )}
                      {visibleColumns.loyaltyPoints && (
                        <th className="pt-2 pb-2 text-center">Loyalty Points</th>
                      )}
                      {visibleColumns.lastOrderDate && (
                        <th className="pt-2 pb-2 text-center">Last Order Date</th>
                      )}
                      {visibleColumns.currentBalance && (
                        <th className="pt-2 pb-2 text-center">Current Balance</th>
                      )}
                      {visibleColumns.refundAmount && (
                        <th className="pt-2 pb-2 text-center">Refund Amount</th>
                      )}
                      {visibleColumns.tipAmount && (
                        <th className="pt-2 pb-2 text-center">Tip Amount</th>
                      )}
                      {visibleColumns.serviceCharges && (
                        <th className="pt-2 pb-2 text-center">Service Charges</th>
                      )}
                      {visibleColumns.extraCharges && (
                        <th className="pt-2 pb-2 text-center">Extra Charges</th>
                      )}
                      {visibleColumns.discountAmount && (
                        <th className="pt-2 pb-2 text-center">Discount</th>
                      )}
                      {visibleColumns.email && (
                        <th className="pt-2 pb-2 text-center">Email</th>
                      )}
                      {visibleColumns.mobile && (
                        <th className="pt-2 pb-2 text-center">Mobile</th>
                      )}
                      {visibleColumns.phone && (
                        <th className="pt-2 pb-2 text-center">Phone</th>
                      )}
                      {visibleColumns.address && (
                        <th className="pt-2 pb-2 text-center">Address</th>
                      )}
                      {visibleColumns.city && (
                        <th className="pt-2 pb-2 text-center">City</th>
                      )}
                      {visibleColumns.country && (
                        <th className="pt-2 pb-2 text-center">Country</th>
                      )}
                      {visibleColumns.postCode && (
                        <th className="pt-2 pb-2 text-center">Post Code</th>
                      )}
                      {visibleColumns.lastOrderType && (
                        <th className="pt-2 pb-2 text-center">Last Order Type</th>
                      )}
                      {visibleColumns.totalEarnedLoyaltyPoints && (
                        <th className="pt-2 pb-2 text-center">Total Earned Points</th>
                      )}
                      {visibleColumns.totalSpentLoyaltyPoints && (
                        <th className="pt-2 pb-2 text-center">Total Spent Points</th>
                      )}
                      {visibleColumns.deposit && (
                        <th className="pt-2 pb-2 text-center">Deposit</th>
                      )}
                      {visibleColumns.maxCredit && (
                        <th className="pt-2 pb-2 text-center">Max Credit</th>
                      )}
                      {visibleColumns.referenceID && (
                        <th className="pt-2 pb-2 text-center">Reference ID</th>
                      )}
                      {visibleColumns.vatNumber && (
                        <th className="pt-2 pb-2 text-center">VAT Number</th>
                      )}
                      {visibleColumns.businessName && (
                        <th className="pt-2 pb-2 text-center">Business Name</th>
                      )}
                      {visibleColumns.dateOfBirth && (
                        <th className="pt-2 pb-2 text-center">Date of Birth</th>
                      )}
                      {visibleColumns.gender && (
                        <th className="pt-2 pb-2 text-center">Gender</th>
                      )}
                      {visibleColumns.title && (
                        <th className="pt-2 pb-2 text-center">Title</th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length + 2} className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="ms-2">Loading...</span>
                        </td>
                      </tr>
                    ) : customers.length === 0 ? (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length + 2} className="text-center py-4">
                          <div className="text-muted">
                            <i className="fa fa-search fa-2x mb-2"></i>
                            <p className="mb-0">
                              {search.trim() ? "No customers found matching your search criteria." : "No customers available."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      customers.filter(customer => customer != null && customer.cust_Guid != null).map((customer, index) => {
                        if (!customer || !customer.cust_Guid) return null;
                        return (
                        <tr key={customer.cust_Guid || index}>
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(customer.cust_Guid)}
                              onChange={(e) =>
                                handleSelectItem(customer.cust_Guid, index, e)
                              }
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          <td
                            className="align-middle"
                            style={{ cursor: "pointer" }}
                          >
                            <div>{(customer.cust_Fname || '') + " " + (customer.cust_Lname || '')}</div>
                            {(customer.cust_Mobile || customer.Cust_Mobile) && (
                              <div className="text-muted small">{customer.cust_Mobile || customer.Cust_Mobile}</div>
                            )}
                          </td>
                          {visibleColumns.totalOrders && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_TotalOrderCount || 0}
                            </td>
                          )}
                          {visibleColumns.totalSpent && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_TotalOrderSpent ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_TotalOrderSpent).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.averageOrderSpent && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_AverageOrderSpent ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_AverageOrderSpent).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.loyaltyPoints && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_LoyaltyPoints || 0}
                            </td>
                          )}
                          {visibleColumns.lastOrderDate && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cus_LastOrderDate ? new Date(customer.cus_LastOrderDate).toLocaleDateString() : 'Never'}
                            </td>
                          )}
                          {visibleColumns.currentBalance && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_CurrentBalance ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_CurrentBalance).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.refundAmount && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_RefundAmount ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_RefundAmount).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.tipAmount && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_TipAmount ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_TipAmount).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.serviceCharges && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_ServiceCharges ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_ServiceCharges).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.extraCharges && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_ExtraCharges ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_ExtraCharges).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.discountAmount && (
                            <td
                              className="align-middle text-center"
                              style={{ cursor: "pointer" }}
                            >
                              {customer.cust_DiscountAmount ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_DiscountAmount).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.email && (
                            <td className="align-middle text-center">
                              {customer.cust_Email || '-'}
                            </td>
                          )}
                          {visibleColumns.mobile && (
                            <td className="align-middle text-center">
                              {customer.cust_Mobile || '-'}
                            </td>
                          )}
                          {visibleColumns.phone && (
                            <td className="align-middle text-center">
                              {customer.cust_Phone || '-'}
                            </td>
                          )}
                          {visibleColumns.address && (
                            <td className="align-middle text-center">
                              {customer.cust_Address || '-'}
                            </td>
                          )}
                          {visibleColumns.city && (
                            <td className="align-middle text-center">
                              {customer.cust_City || customer.cust_CityName || '-'}
                            </td>
                          )}
                          {visibleColumns.country && (
                            <td className="align-middle text-center">
                              {customer.cust_Country || '-'}
                            </td>
                          )}
                          {visibleColumns.postCode && (
                            <td className="align-middle text-center">
                              {customer.cust_PostCode || '-'}
                            </td>
                          )}
                          {visibleColumns.lastOrderType && (
                            <td className="align-middle text-center">
                              {customer.cust_LastOrderType || '-'}
                            </td>
                          )}
                          {visibleColumns.totalEarnedLoyaltyPoints && (
                            <td className="align-middle text-center">
                              {customer.cust_TotalEarnedLoyaltyPoints || 0}
                            </td>
                          )}
                          {visibleColumns.totalSpentLoyaltyPoints && (
                            <td className="align-middle text-center">
                              {customer.cust_TotalSpentLoyaltyPoints || 0}
                            </td>
                          )}
                          {visibleColumns.deposit && (
                            <td className="align-middle text-center">
                              {customer.cust_Deposit ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_Deposit).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.maxCredit && (
                            <td className="align-middle text-center">
                              {customer.cust_MaxCredit ? `${getCurrencySymbol(compCurrency)}${parseFloat(customer.cust_MaxCredit).toFixed(2)}` : `${getCurrencySymbol(compCurrency)}0.00`}
                            </td>
                          )}
                          {visibleColumns.referenceID && (
                            <td className="align-middle text-center">
                              {customer.cust_Reference_ID || '-'}
                            </td>
                          )}
                          {visibleColumns.vatNumber && (
                            <td className="align-middle text-center">
                              {customer.cust_VAT_Number || '-'}
                            </td>
                          )}
                          {visibleColumns.businessName && (
                            <td className="align-middle text-center">
                              {customer.cust_BusinessName || '-'}
                            </td>
                          )}
                          {visibleColumns.dateOfBirth && (
                            <td className="align-middle text-center">
                              {customer.cust_DOB ? new Date(customer.cust_DOB).toLocaleDateString() : '-'}
                            </td>
                          )}
                          {visibleColumns.gender && (
                            <td className="align-middle text-center">
                              {customer.cust_Gender || '-'}
                            </td>
                          )}
                          {visibleColumns.title && (
                            <td className="align-middle text-center">
                              {customer.cust_Title || '-'}
                            </td>
                          )}
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Component */}
              <Pagination
                totalRecords={totalRecords}
                pageSize={pageSize}
                pageNumber={pageNumber}
                totalPages={totalPages}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReport;